import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Bell, BellOff, CheckCheck, ShoppingCart, AlertTriangle, Truck, Wallet, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Notifications() {
  const { user } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);
  const [notifications, setNotifications] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [res, compsRes] = await Promise.all([
        api.getNotifications(),
        api.getCompanies(true).catch(() => ({ data: [] })),
      ]);
      setNotifications(res.data);
      setCompanies(compsRes.data || []);
    } catch (e) {
      toast.error('SalesPRO: Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    } catch (e) { /* ignore */ }
  };

  const markAll = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('SalesPRO: Todas marcadas como leídas');
    } catch (e) {
      toast.error('SalesPRO: Error al marcar todas');
    }
  };

  const companyName = (id) => companies.find(c => c.company_id === id)?.name || 'General';

  const iconFor = (type) => {
    if (type === 'client_order') return <ShoppingCart size={18} className="text-primary" />;
    if (type === 'over_stock' || type === 'inventory_alert') return <AlertTriangle size={18} className="text-red-600" />;
    if (type === 'dispatch_status') return <Truck size={18} className="text-blue-600" />;
    if (type === 'payroll') return <Wallet size={18} className="text-green-600" />;
    if (type === 'credit_request') return <Wallet size={18} className="text-orange-600" />;
    return <Bell size={18} className="text-[#64748B]" />;
  };

  const isAlert = (n) => n.severity === 'alert' || n.type === 'inventory_alert';

  const filtered = useMemo(() => (
    companyFilter ? notifications.filter(n => n.company_id === companyFilter) : notifications
  ), [notifications, companyFilter]);

  // Agrupar por empresa
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(n => {
      const key = n.company_id || '_general';
      (g[key] = g[key] || []).push(n);
    });
    return g;
  }, [filtered]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const companiesWithNotifs = [...new Set(notifications.map(n => n.company_id).filter(Boolean))];

  if (loading) return <div className="flex items-center justify-center h-full">Cargando SalesPRO...</div>;

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex justify-between items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Notificaciones</h1>
          <p className="text-sm text-[#64748B]">{unreadCount > 0 ? `${unreadCount} sin leer` : 'Todo al día'}</p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read">
            <CheckCheck size={14} className="mr-1" /> Marcar todas
          </Button>
        )}
      </div>

      {/* Filtro por empresa (master) */}
      {isMaster && companiesWithNotifs.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="notif-company-filters">
          <button
            onClick={() => setCompanyFilter('')}
            className={`px-3 py-1 rounded-full text-xs border ${!companyFilter ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white text-[#64748B] border-[#E2E8F0]'}`}
          >
            Todas
          </button>
          {companiesWithNotifs.map(cid => {
            const cnt = notifications.filter(n => n.company_id === cid && !n.read).length;
            return (
              <button
                key={cid}
                onClick={() => setCompanyFilter(cid)}
                className={`px-3 py-1 rounded-full text-xs border flex items-center gap-1 ${companyFilter === cid ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white text-[#64748B] border-[#E2E8F0]'}`}
                data-testid={`notif-filter-${cid}`}
              >
                <Building2 size={11} /> {companyName(cid)}
                {cnt > 0 && <span className="ml-1 bg-primary text-white rounded-full px-1.5 text-[10px]">{cnt}</span>}
              </button>
            );
          })}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <BellOff size={48} className="mx-auto text-[#CBD5E1] mb-3" />
          <p className="text-[#64748B]">Aún no tienes notificaciones</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([cid, items]) => (
            <div key={cid} data-testid={`notif-group-${cid}`}>
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={14} className="text-[#94A3B8]" />
                <h2 className="text-sm font-semibold text-[#475569]">{cid === '_general' ? 'General' : companyName(cid)}</h2>
                <span className="text-xs text-[#94A3B8]">({items.length})</span>
              </div>
              <div className="space-y-2">
                {items.map(n => (
                  <Card
                    key={n.notification_id}
                    className={`border cursor-pointer ${
                      isAlert(n)
                        ? (n.read ? 'border-red-200 bg-red-50/40' : 'border-red-400 bg-red-50')
                        : (n.read ? 'border-[#E2E8F0] bg-white' : 'border-primary/30 bg-primary/5')
                    }`}
                    onClick={() => !n.read && markRead(n.notification_id)}
                    data-testid={`notification-${n.notification_id}`}
                  >
                    <CardContent className="p-3 flex gap-3 items-start">
                      <div className={`p-2 rounded-full ${isAlert(n) ? 'bg-red-100' : (n.read ? 'bg-[#F8F9FA]' : 'bg-primary/10')}`}>
                        {iconFor(n.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold ${isAlert(n) ? 'text-red-700' : 'text-[#022C22]'} ${!n.read ? '' : 'font-medium'}`}>{n.title}</p>
                        <p className={`text-xs mt-1 ${isAlert(n) ? 'text-red-600' : 'text-[#64748B]'}`}>{n.message}</p>
                        <p className="text-[10px] text-[#94A3B8] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      {!n.read && <div className={`w-2 h-2 rounded-full shrink-0 mt-2 ${isAlert(n) ? 'bg-red-500' : 'bg-primary'}`} />}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
